import { useCallback, useEffect, useId, useState } from 'react';
import { api } from '../../api.js';
import Field from '../Field.jsx';
import Alert from '../ui/Alert.jsx';
import ConfirmModal from '../ui/ConfirmModal.jsx';
import Modal, { ModalActions } from '../ui/Modal.jsx';
import { Section } from '../ui/index.jsx';
import { fmtDay } from '../../utils/format.js';

const TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];
const MAX_BYTES = 5 * 1024 * 1024;

/** A file's problem before uploading (the server checks again), or ''. */
function fileProblem(f) {
  if (!f) return 'Choose an image file.';
  if (!TYPES.includes(f.type)) return 'Use a PNG, JPG, WebP or SVG file.';
  if (f.size > MAX_BYTES) return 'That file is too big — the limit is 5 MB.';
  return '';
}

function UploadModal({ base, ownerName, as: initialAs, hasCurrent, onClose, onDone }) {
  const inputId = useId();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [as, setAs] = useState(initialAs);
  const [keepOld, setKeepOld] = useState(true);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const pick = (f) => {
    setError(fileProblem(f));
    setFile(f || null);
    setPreview(f && !fileProblem(f) ? URL.createObjectURL(f) : '');
  };

  const save = async () => {
    const problem = fileProblem(file);
    if (problem) { setError(problem); return; }
    if (note.length > 200) { setError('Keep the note to 200 characters or fewer.'); return; }
    setBusy(true); setError('');
    const q = new URLSearchParams({ as });
    if (as === 'current' && hasCurrent && !keepOld) q.set('drop_old', '1');
    if (as === 'retired' && note.trim()) q.set('note', note.trim());
    try { onDone(await api(`${base}?${q}`, { method: 'POST', file })); } catch (err) { setError(err.message); setBusy(false); }
  };

  return (
    <Modal title={as === 'current' ? (hasCurrent ? 'Replace the logo' : 'Upload your logo') : 'Add a previous logo'} onClose={onClose} onSubmit={save} busy={busy}
      description={as === 'current'
        ? `Shown on ${ownerName}’s public verify page, so people can see they’ve found the real thing. We also compare it with the logos in emails people forward to us.`
        : 'A logo you used to use. It’s never shown publicly. Scammers often copy an old logo from an old email, so an email using one is a warning sign.'}
      footer={<ModalActions submitLabel="Upload" busyLabel="Uploading…" busy={busy} onCancel={onClose} disabled={!file || !!fileProblem(file)} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="field">
        <label htmlFor={inputId}>Image file</label>
        <input id={inputId} type="file" accept={TYPES.join(',')} onChange={(e) => pick(e.target.files?.[0])} />
        <p className="field-hint">PNG, JPG, WebP or SVG, up to 5 MB, at least 48 pixels across. A wide version with a transparent or white background works best.</p>
      </div>
      {preview && <div className="logo-preview"><img src={preview} alt="The logo you chose" /></div>}
      <fieldset className="fieldset">
        <legend>Is this the logo you use now?</legend>
        <label className="radio"><input type="radio" name="as" checked={as === 'current'} onChange={() => setAs('current')} /> <span>Yes — our current logo</span></label>
        <label className="radio"><input type="radio" name="as" checked={as === 'retired'} onChange={() => setAs('retired')} /> <span>No — one we used to use</span></label>
      </fieldset>
      {as === 'current' && hasCurrent && (
        <label className="checkbox">
          <input type="checkbox" checked={keepOld} onChange={(e) => setKeepOld(e.target.checked)} />
          <span>Keep the old one as a previous logo <span className="muted small">(recommended: we’ll flag emails still using it)</span></span>
        </label>
      )}
      {as === 'retired' && (
        <Field label="When you used it" optional maxLength={200} counter value={note} onChange={(e) => setNote(e.target.value)} placeholder="Used until 2024" />
      )}
    </Modal>
  );
}

/**
 * The official logo of a company, brand, event or supplier, and the ones it used before.
 * base: /portal/organisations/:orgId ; entityId: whose logos.
 */
export default function LogoSection({ base, entityId, ownerName, canEdit, title = 'Logo', description }) {
  const path = `${base}/entities/${entityId}/logos`;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);   // { upload: 'current'|'retired' } | { remove: logo } | { promote: logo }
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try { setData(await api(path)); } catch (err) { setError(err.message); }
  }, [path]);
  useEffect(() => { load(); }, [load]);

  const done = (d, text) => { setData(d); setModal(null); setNotice(text); };

  return (
    <Section title={title}
      description={description || `Shown on the public verify page, next to ${ownerName}’s details. We also compare it with the images in emails people forward to us: an email using an old or altered version of it is a warning sign.`}
      actions={canEdit && data && (
        <span className="head-buttons">
          <button type="button" className="btn btn-primary btn-small" onClick={() => setModal({ upload: 'current' })}>{data.current ? 'Replace logo' : 'Upload logo'}</button>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => setModal({ upload: 'retired' })}>Add a previous logo</button>
        </span>
      )}>
      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
      {!data ? <p className="muted">Loading…</p> : (
        <div className="logo-block">
          <div className="logo-current">
            {data.current
              ? <div className="logo-frame"><img src={data.current.url} alt={`${ownerName}’s logo`} /></div>
              : <div className="logo-frame logo-empty"><span>No logo yet</span></div>}
            {data.current && <p className="muted small">Uploaded {fmtDay(data.current.created_at)}</p>}
          </div>
          <div className="logo-previous">
            <h3 className="subhead">Previous logos</h3>
            {data.previous.length === 0
              ? <p className="muted small">None. If {ownerName} has changed its logo, add the old ones: emails still using them are flagged.</p>
              : (
                <ul className="logo-list">
                  {data.previous.map((l) => (
                    <li key={l.id}>
                      <div className="logo-frame logo-frame-small"><img src={l.url} alt={`A previous logo of ${ownerName}`} /></div>
                      <div className="logo-list-text">
                        <span>{l.note || `Retired ${fmtDay(l.retired_at || l.created_at)}`}</span>
                        {canEdit && (
                          <span className="row-buttons">
                            <button type="button" className="link-button small" onClick={() => setModal({ promote: l })}>Make current</button>
                            <button type="button" className="link-button small danger" onClick={() => setModal({ remove: l })}>Remove</button>
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      )}

      {modal?.upload && (
        <UploadModal base={path} ownerName={ownerName} as={modal.upload} hasCurrent={!!data?.current} onClose={() => setModal(null)}
          onDone={(d) => done(d, modal.upload === 'current' ? 'Logo saved. It’s on the public page now.' : 'Previous logo added.')} />
      )}
      {modal?.promote && (
        <ConfirmModal title="Make this the current logo?" confirmLabel="Make current" onClose={() => setModal(null)}
          onConfirm={async () => done(await api(`${path}/${modal.promote.id}`, { method: 'PATCH', body: { status: 'current' } }), 'Current logo changed.')}>
          <p>It replaces the logo on the public page. The one there now becomes a previous logo.</p>
        </ConfirmModal>
      )}
      {modal?.remove && (
        <ConfirmModal title="Remove this previous logo?" confirmLabel="Remove" danger onClose={() => setModal(null)}
          onConfirm={async () => done(await api(`${path}/${modal.remove.id}`, { method: 'DELETE' }), 'Removed.')}>
          <p>We’ll stop flagging emails that use it.</p>
        </ConfirmModal>
      )}
    </Section>
  );
}
